import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
    ReactFlow,
    Background,
    Controls,
    Node,
    Edge,
    Position,
    ReactFlowInstance,
    ReactFlowProps,
} from '@xyflow/react';
import dagre from 'dagre';
import CustomNode from '../Sources/Tabs/CustomNode';
import { buildTreeFromFlatList, SourceTreeNode } from '../../helpers/buildTree';
import { ThunkDispatch } from '@reduxjs/toolkit';
import { useDispatch, useSelector } from 'react-redux';
import { Action } from 'redux';
import { ApplicationState } from 'store';
import { sourceCreateRequest, sourceGetRequest, sourceListGetRequest, sourceUpdateRequest } from 'store/sources/action';
import { optionsListGetRequest } from 'store/auth/action';
import { ToastContainer } from 'react-toastify';
import AddEntryModal from 'Components/Common/Views/innerComponent/AddEntryModal';
import { withTranslation } from 'react-i18next';
import EditEntryModal from 'Components/Common/Views/innerComponent/EditEntryModal';
import DeleteModal from './Actions/DeleteModal';

const nodeWidth = 300;
const nodeHeight = 36;

const placeholderNodeStyle: React.CSSProperties = {
    width: `${50}px`,
    height: `${nodeHeight}px`,
};

const getLayoutedElements = (
    nodes: Node[],
    edges: Edge[],
    direction: 'TB' | 'LR' = 'TB'
): { nodes: Node[]; edges: Edge[] } => {
    const dagreGraph = new dagre.graphlib.Graph();
    dagreGraph.setDefaultEdgeLabel(() => ({}));
    dagreGraph.setGraph({ rankdir: direction, ranksep: 100, nodesep: 50 });

    nodes.forEach((node) => {
        dagreGraph.setNode(node.id, { width: nodeWidth, height: nodeHeight });
    });

    edges.forEach((edge) => {
        dagreGraph.setEdge(edge.source, edge.target);
    });

    dagre.layout(dagreGraph);

    const layoutedNodes: Node[] = nodes.map((node) => {
        const nodeWithPosition = dagreGraph.node(node.id);
        const isHorizontal = direction === 'LR';

        node.targetPosition = (isHorizontal ? 'left' : 'top') as Position;
        node.sourcePosition = (isHorizontal ? 'right' : 'bottom') as Position;
        node.position = {
            x: nodeWithPosition.x - nodeWidth / 2,
            y: nodeWithPosition.y - nodeHeight / 2,
        };
        return node;
    });

    return { nodes: layoutedNodes, edges };
};

const SourcesTree: React.FC = (props: any) => {
    const dispatch: ThunkDispatch<any, null, Action<string>> = useDispatch();
    const sourcesList = useSelector((state: ApplicationState) => state.sources.list);
    const sourceMetadata = useSelector((state: ApplicationState) => state.sources.metaDataFields);
    const sourceDetails = useSelector((state: ApplicationState) => state.sources.details);
    const dataFieldsLoading = useSelector((state: ApplicationState) => state.sources.dataLoading);
    const [sourceData, setSourceData] = useState<any>(null)
    const [isCreateModal, setIsCreateModal] = useState(false);
    const [isEditModal, setIsEditModal] = useState(false);
    const [deleteRecord, setDeleteRecord] = useState<any>(null);
    const [parentMeta, setParentMeta] = useState<{ parentId: string; parentName: string }>({
        parentId: '',
        parentName: ''
    });
    const [triggerApi, setTriggerApi]: any = useState(false);
    const [reactFlowInstance, setReactFlowInstance] = useState<ReactFlowInstance | null>(null);
    const filter = {
        filters: [
            {
                quick: [],
                advance: [],
                search: null,
            },
        ],
        sorts: [
            {
                field: 'name',
                order: 'asc',
            },
        ],
    };
    useEffect(() => {
        const filterObject = { responseType: 'treeselect' };
        dispatch(sourceListGetRequest(filter, 0, 500));
        dispatch(optionsListGetRequest('sources', filter, 0, 500, filterObject, 'field'));
    }, [dispatch]);

    const tree = useMemo<SourceTreeNode[]>(() => {
        return sourcesList?.length ? buildTreeFromFlatList(sourcesList) : [];
    }, [sourcesList]);

    useEffect(() => {
        if (
            sourceDetails &&
            sourceDetails.valuesJson &&
            Object.keys(sourceDetails.valuesJson).length > 0
        ) {
            setSourceData(sourceDetails);
        }
    }, [sourceDetails]);

    const handleEditSource = (sourceId: any) => {
        dispatch(sourceGetRequest(sourceId));
        if (sourceData !== null || sourceData !== undefined) {
            setIsEditModal(true);
        }
    };

    const { nodes, edges } = useMemo(() => {
        const n: Node[] = [];
        const e: Edge[] = [];

        const traverse = (node: SourceTreeNode): void => {
            const values = node.valuesJson || {};
            const nodeName = values.name || 'Unnamed';

            const parentId = values.parent?.id || null;
            const parentName = values.parent?.name || null;
            const createdById = values.created_by?.id || null;
            const createdByName = values.created_by?.name || null;

            n.push({
                id: node.id,
                type: 'custom',
                data: {
                    id: node.id,
                    name: nodeName,
                    label: nodeName,
                    parentId,
                    parentName,
                    createdById,
                    createdByName,
                    isDeleted: !!node.deletedAt,
                    onAdd: () => {
                        setParentMeta({
                            parentId: node.id,
                            parentName: nodeName
                        });
                        setIsCreateModal(true);
                    },
                    onEdit: () => {
                        setParentMeta({
                            parentId: parentId ? parentId : '',
                            parentName: nodeName
                        });
                        handleEditSource(node.id);
                    },
                    onDelete: setDeleteRecord
                },
                position: { x: 0, y: 0 },
            });

            node.children?.forEach((child) => {
                e.push({
                    id: `e-${node.id}-${child.id}`,
                    source: node.id,
                    target: child.id,
                });
                traverse(child);
            });
        };

        tree.forEach(traverse);
        return getLayoutedElements(n, e, 'TB');
    }, [tree]);

    const onInit: ReactFlowProps['onInit'] = useCallback((instance: ReactFlowInstance) => {
        setReactFlowInstance(instance);
    }, []);

    useEffect(() => {
        if (reactFlowInstance && nodes.length > 0) {
            reactFlowInstance.fitView({ padding: 0.1 }); // 10% padding
        }
    }, [nodes, reactFlowInstance]);

    const nodeTypes = useMemo(() => ({ custom: CustomNode }), []);
    return (
        <>
            <ToastContainer />
            {isCreateModal && (
                <AddEntryModal
                    show={isCreateModal}
                    onCloseClick={() => setIsCreateModal(false)}
                    dataFields={sourceMetadata}
                    setTriggerApi={setTriggerApi}
                    props={props}
                    filter={filter}
                    createAction={sourceCreateRequest}
                    listActions={sourceListGetRequest}
                    parent="parent" // <- the key expected in the form
                    parentId={parentMeta.parentId}
                    parentName={parentMeta.parentName} // optional, but useful if you want to show it
                />
            )}
            {sourceData && isEditModal && <EditEntryModal
                id={sourceData.id}
                show={isEditModal}
                onCloseClick={() => {
                    setSourceData(null)
                    setIsEditModal(false)
                }}
                listActions={sourceListGetRequest}
                filter={filter}
                dataFields={sourceMetadata}
                setTriggerApi={setTriggerApi}
                props={props}
                updateAction={sourceUpdateRequest}
                data={sourceData}
                parentId={parentMeta.parentId}
                parentName={parentMeta.parentName}
            />}
            <DeleteModal
                show={!!deleteRecord}
                onCloseClick={() => setDeleteRecord(null)}
                record={deleteRecord}
            />
            <div style={{ height: '90vh' }}>
                {dataFieldsLoading ? (
                    <div className="placeholder-glow d-flex flex-column align-items-center gap-4 h-100 p-4 overflow-auto">
                        <span className="placeholder rounded" style={placeholderNodeStyle} />
                        <div className="d-flex justify-content-between w-100 px-5">
                            <span className="placeholder rounded" style={placeholderNodeStyle} />
                            <span className="placeholder rounded" style={placeholderNodeStyle} />
                        </div>
                        <div className="d-flex justify-content-between w-100 px-5 flex-wrap gap-4">
                            {Array.from({ length: 4 }).map((_, idx) => (
                                <div key={`l2-${idx}`} className="d-flex flex-column gap-3">
                                    <span className="placeholder rounded" style={placeholderNodeStyle} />
                                    <span className="placeholder rounded" style={placeholderNodeStyle} />
                                </div>
                            ))}
                        </div>
                        <div className="d-flex justify-content-between w-100 px-5 flex-wrap gap-4">
                            {Array.from({ length: 6 }).map((_, idx) => (
                                <div key={`l3-${idx}`} className="d-flex flex-column gap-3">
                                    <span className="placeholder rounded" style={placeholderNodeStyle} />
                                    <span className="placeholder rounded" style={placeholderNodeStyle} />
                                </div>
                            ))}
                        </div>
                    </div>
                ) : nodes.length === 0 ? (
                    <div className="d-flex flex-column justify-content-start align-items-center h-100 text-center px-4 mt-4">
                        <p className="text-muted mb-3">No Sources found</p>
                        <button className="btn btn-primary" onClick={() => setIsCreateModal(true)}>
                            Create First Source
                        </button>
                    </div>
                ) : (
                    <ReactFlow
                        nodes={nodes}
                        edges={edges}
                        nodeTypes={nodeTypes}
                        onInit={onInit}
                    >
                        <Background />
                        <Controls />
                    </ReactFlow>
                )}
            </div>
        </>
    );
};

export default withTranslation()(SourcesTree);